import { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import "./index.css";
import { abbreviate, densityFor, isCJK, tabWidthFor } from "./density";
import { todayScrollTarget } from "./scroll";
import { tickedSlots } from "../../sync/doc";

/** Build the visible days from range + recurrence (same rule as before). */
export function buildDays({ startFrom, numberOfDays, repeatStep, repeatUnit }) {
  const days = [];
  const total = Number(numberOfDays) || 0;
  const step = Math.max(1, Number(repeatStep) || 1);
  for (let i = 0; i < total; i++) {
    if (repeatUnit === "day" && i % step !== 0) continue;
    if (repeatUnit === "week" && Math.floor(i / 7) % step !== 0) continue;
    days.push(dayjs(startFrom).add(i, "day"));
  }
  return days;
}

function Tray(props) {
  const { slots, ticks, dateFormat, weekdayFormat, showWeekdays, onToggleTick, title } = props;
  const days = buildDays(props);
  const trayRef = useRef(null);
  const [density, setDensity] = useState({
    compact: false,
    wrap: false,
    stack: false,
    tight: false,
    tier: "full",
    perLid: Infinity,
    tabMax: 240,
  });
  const cols = Math.max(1, slots.length);
  const today = dayjs();

  // Density is measured, not assumed: read the tray, decide what fits.
  useEffect(() => {
    const el = trayRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    let cancelled = false;
    const measure = () => {
      if (cancelled) return;
      const trayWidth = el.getBoundingClientRect().width;
      // Measure the lettering, not the tab. The tab's own box changes shape
      // when the strip stacks, so reading it back would feed the stack
      // decision its own consequence; the text is the one quantity that means
      // the same thing in either layout, and tabWidthFor rebuilds the rest.
      // scrollWidth, so an ellipsised date still reports its full width.
      const date = el.querySelector(".Tab-date");
      const weekday = el.querySelector(".Tab-weekday");
      const textWidth = Math.max(date ? date.scrollWidth : 0, weekday ? weekday.scrollWidth : 0);
      const next = densityFor({ trayWidth, tabWidth: tabWidthFor({ textWidth, trayWidth }), cols });
      el.style.setProperty("--tab-max", next.tabMax + "px");
      setDensity((prev) =>
        prev.tier === next.tier &&
        prev.compact === next.compact &&
        prev.wrap === next.wrap &&
        prev.stack === next.stack &&
        prev.tight === next.tight &&
        prev.tabMax === next.tabMax
          ? prev
          : next
      );
    };
    measure();
    // The first pass runs before the compact class lands and before Barlow has
    // swapped in, both of which change the lettering's width. Neither resizes
    // the tray, so the observer would never fire for them.
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure).catch(() => {});
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => {
      cancelled = true;
      ro.disconnect();
    };
    // density.compact is a dependency for the same reason: it changes the
    // date's font size, so the measurement has to be retaken once it settles.
  }, [cols, showWeekdays, dateFormat, weekdayFormat, density.compact]);

  // On load, centre today if the range holds it but the window does not show
  // it. Once per load: after that, where the page sits is the user's business.
  //
  // Not on the first paint. Density restacks the strips after it, and again
  // when Barlow swaps in: at 390px, five columns, today 20 days into the range,
  // that moved today 408px down the page, so an early scroll lands on the wrong
  // day. Wait for the fonts, then for the tray to hold its height for a few
  // frames. Frames rather than milliseconds, because a slow phone is slow at
  // both the restack and the frames.
  useEffect(() => {
    const el = trayRef.current;
    if (!el) return;
    let done = false;
    let frame = 0;
    const inputs = ["wheel", "touchstart", "keydown", "mousedown"];
    const stop = () => {
      done = true;
      cancelAnimationFrame(frame);
      inputs.forEach((type) => window.removeEventListener(type, stop));
    };
    // Someone already moving the page is not yanked back to today.
    inputs.forEach((type) => window.addEventListener(type, stop, { passive: true }));

    const scrollToToday = () => {
      const strip = el.querySelector(".Strip.is-today");
      if (!strip) return;
      const { top, bottom } = strip.getBoundingClientRect();
      const bar = document.querySelector(".LabelBar");
      const target = todayScrollTarget({
        top,
        bottom,
        coveredTop: bar ? Math.max(0, bar.getBoundingClientRect().bottom) : 0,
        viewportHeight: window.innerHeight,
        scrollY: window.scrollY,
      });
      if (target !== null) window.scrollTo(0, target);
    };
    const settle = (lastHeight, stillFrames, frames) => {
      frame = requestAnimationFrame(() => {
        if (done) return;
        const height = el.getBoundingClientRect().height;
        const still = height === lastHeight ? stillFrames + 1 : 0;
        // Capped at 60 frames, so a layout that never settles is scrolled anyway.
        if (still < 3 && frames < 60) {
          settle(height, still, frames + 1);
          return;
        }
        scrollToToday();
        stop();
      });
    };
    const fonts = document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve();
    fonts
      .then(() => {
        if (!done) settle(-1, 0, 0);
      })
      .catch(() => {});
    return stop;
  }, []);

  const displayFormat = showWeekdays ? `${dateFormat} (${weekdayFormat})` : dateFormat;

  return (
    <section
      className="Tray screen-only"
      ref={trayRef}
      data-tier={density.tier}
      data-compact={density.compact ? "true" : "false"}
      data-wrap={density.wrap ? "true" : "false"}
      data-stack={density.stack ? "true" : "false"}
      data-tight={density.tight ? "true" : "false"}
      aria-label="Schedule"
    >
      {title ? (
        <div className="Tray-sticker" aria-hidden="true">
          {title}
        </div>
      ) : null}
      <h2 className="sr-only">{title || "Schedule"}</h2>
      {days.length === 0 ? (
        <p className="Tray-empty">No days in this range. Pick a start and end date on the label above.</p>
      ) : null}
      {days.map((day) => {
        const checked = tickedSlots(ticks, day);
        const key = day.valueOf();
        const isToday = day.isSame(today, "day");
        const label = day.format(displayFormat);
        return (
          <div className={`Strip${isToday ? " is-today" : ""}`} key={key} style={{ "--cols": cols }}>
            <div className="Tab">
              {showWeekdays ? <span className="Tab-weekday">{day.format(weekdayFormat)}</span> : null}
              <span className="Tab-date">{day.format(dateFormat)}</span>
            </div>
            {isToday ? (
              <span className="Tab-today" title="Today">
                <span className="sr-only">Today</span>
              </span>
            ) : null}
            <div className="Cells" role="group" aria-label={label}>
              {slots.map((slot) => {
                const isPressed = checked.includes(slot.id);
                return (
                  <div className="Cell" key={slot.id}>
                    <div className="Well" aria-hidden="true" />
                    <button
                      type="button"
                      role="checkbox"
                      aria-checked={isPressed}
                      aria-label={`${slot.name}, ${label}`}
                      className={`Plate${isPressed ? " is-pressed" : ""}`}
                      onClick={() => onToggleTick(day, slot.id, !isPressed)}
                    >
                      <span className="Plate-label" title={slot.name} data-cjk={isCJK(slot.name) ? "true" : undefined}>
                        {abbreviate(slot.name, density.tier)}
                      </span>
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </section>
  );
}

export default Tray;
